import type {GridFilterModel} from "@mui/x-data-grid-premium";
import {
    SUPPLIER_CLUSTER_MEMBER_MATCH_BASES,
    type SupplierClusterMemberListParams,
    type SupplierClusterMemberMatchBasis,
} from "~/pages/supplier-360/types/Supplier360Types";
import {asNumber, asStringArray} from "~/pages/supplier-360/components/gridFilterValues";

const MATCH_BASES = new Set<string>(SUPPLIER_CLUSTER_MEMBER_MATCH_BASES);

export type ClusterMemberColumnFilters = Pick<SupplierClusterMemberListParams,
    | 'normalized_name_contains'
    | 'data_source'
    | 'country_code_a2'
    | 'vat'
    | 'vat_contains'
    | 'match_basis'
    | 'spend_gte'
    | 'spend_lte'>;

function isExactOrAnyOf(operator: string): boolean {
    return operator === 'is' || operator === 'equals' || operator === 'isAnyOf';
}

/**
 * Maps DataGrid filter items to `supplier_360/cluster_member/` query params (§2.2). Only the
 * operators each column offers reach here: `contains` for the raw name, exact / list for Source,
 * Country and Match basis, `equals` / `contains` for VAT, and `>=` / `<=` for Spend.
 * "Resolves to" (`entity`) is not a column filter.
 */
export function clusterMemberListParamsFromFilterModel(model: GridFilterModel): ClusterMemberColumnFilters {
    const params: ClusterMemberColumnFilters = {};

    for (const item of model.items) {
        if (!item.field) continue;

        if (item.field === 'spend') {
            const value = asNumber(item.value);
            if (value === undefined) continue;
            if (item.operator === '>=') params.spend_gte = value;
            if (item.operator === '<=') params.spend_lte = value;
            continue;
        }

        const values = asStringArray(item.value);
        if (values.length === 0) continue;

        switch (item.field) {
            case 'normalized_name':
                if (item.operator === 'contains') params.normalized_name_contains = values[0];
                break;
            case 'data_source':
                if (isExactOrAnyOf(item.operator)) {
                    const ids = values.map(Number).filter(Number.isFinite);
                    if (ids.length > 0) params.data_source = ids;
                }
                break;
            case 'country':
                if (isExactOrAnyOf(item.operator)) params.country_code_a2 = values;
                break;
            case 'vat':
                if (item.operator === 'equals') params.vat = values[0];
                if (item.operator === 'contains') params.vat_contains = values[0];
                break;
            case 'match_basis':
                if (isExactOrAnyOf(item.operator)) {
                    const bases = values.filter(v => MATCH_BASES.has(v)) as SupplierClusterMemberMatchBasis[];
                    if (bases.length > 0) params.match_basis = bases;
                }
                break;
        }
    }

    return params;
}
